import { bound } from "@frui.ts/helpers";
import { ScreenBase, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { addError, getValidationMessage, removeError, validate } from "@frui.ts/validation";
import download from "downloadjs";
import toUpper from "lodash/toUpper";
import { computed, observable } from "mobx";
import { Modules } from "config";
import Distributor from "entities/distributor";
import DistributorStorno, { DistributorStornoValidationRules } from "entities/distributorStorno";
import type DistributorRepository from "repositories/distributorRepository";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import { attachAutomaticValidator, attachManualValidator, attachValidatorAggregator } from "utils/validatorAggregator";

export default class DistributorStornoViewModel extends ScreenBase {
  @observable item: DistributorStorno;
  @observable distributors: Distributor[] = [];
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable uploading: boolean;
  @observable response?: any;
  @observable error?: string;
  @observable private dummy = 0;

  constructor(
    private localizationService: LocalizationService,
    private repository: DistributorRepository,
    private optionsService: OptionsService
  ) {
    super();
    this.item = new DistributorStorno();
    attachValidatorAggregator(this, [attachAutomaticValidator(this.item, DistributorStornoValidationRules)]);
    attachManualValidator(this);
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get cancellationRecordId() {
    return this.item.cancellationRecordId;
  }

  set cancellationRecordId(recordNumber: string) {
    this.item.cancellationRecordId = toUpper(recordNumber);
  }

  @computed
  get cancellationRecordIdError() {
    return getValidationMessage(this.item, "cancellationRecordId");
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  onDrop(files: File[]) {
    this.item.file = files[0];
  }

  @bound
  onRemoveFileClick() {
    removeError(this, "item");
    this.item.file = undefined;
    this.dummy++;
  }

  @computed
  get fileError() {
    void this.dummy;
    return getValidationMessage(this, "item") ?? getValidationMessage(this.item, "file");
  }

  @bound
  @watchBusy
  async onSubmit() {
    if (validate(this)) {
      try {
        this.uploading = true;
        this.response = await this.repository.distributorStorno(this.item);
        this.uploading = false;
      } catch (e: any) {
        this.uploading = false;
        if (e.response.status === 400) {
          addError(this, "item", this.translate("invalidFile"));
          validate(this);
          this.dummy++;
        } else {
          this.error = this.translate("unexpectedError");
        }
      }
    }
  }

  @bound
  onDownloadClick() {
    download(
      this.response.data,
      `${this.item.distributorId}-${this.item.cancellationRecordId}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
    this.requestClose();
  }

  @bound
  onResetClick() {
    this.error = undefined;
    this.response = undefined;
    this.onRemoveFileClick();
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `distributorStorno.${code}`, size);
  }

  translateAttribute(attribute: keyof DistributorStorno) {
    return this.localizationService.translateAttribute("distributorStorno", attribute);
  }
}
